import type {
  CreditedInitialCosts, FxValues, InitialCosts, InitialInsurancePayment, InsurancePayment, MonthlyFees, NonMonthlyFees, Offer, PaymentDuration,
} from '@/models/simulation/subtypes';

type Period = { from: number; to: number };
/** Keys present on both {@link MonthlyFees} and {@link NonMonthlyFees} with value type `InsurancePayment[]` on each. */
type InsurancePaymentKeys = KeysOfValue<MonthlyFees | NonMonthlyFees, InsurancePayment[]>;
/** Keys present on {@link InitialCosts} with value type `InitialInsurancePayment` on each. */
type InitialInsurancePaymentKeys = KeysOfValue<CreditedInitialCosts | InitialCosts, InitialInsurancePayment>;

/** Everything the `combined-costs` row shows, in PLN and in the loan currency - the offers table, the printout and the offer sorting read it from here. */
export const useSimulationCombinedCosts = () => {
  const getCombinedCostsAmount = (offer: Offer): number => offer.totals.costsSum;
  const fxCombinedCostsCallback = (fxValues: FxValues) => fxValues.costsSum;

  const getInterestAmount = (offer: Offer): number => offer.totals.interest;
  const fxInterestCallback = (fxValues: FxValues) => fxValues.interest;

  const getProvisionAmount = (offer: Offer) => offer.totals.provision;
  const fxProvisionCallback = (fxValues: FxValues) => fxValues.provision;

  const getRealEstateInsuranceAmount = (offer: Offer) => offer.totals.realEstateInsurance ?? 0;
  const fxRealEstateInsuranceCallback = (fxValues: FxValues) => fxValues.realEstateInsurance?.total;

  const getLifeInsuranceAmount = (offer: Offer) => offer.totals.lifeInsurance ?? 0;
  const fxLifeInsuranceCallback = (fxValues: FxValues) => fxValues.lifeInsurance?.total;

  const getGuaranteeProvisionAmount = (offer: Offer) => offer.totals.guaranteeProvision ?? 0;
  const fxGuaranteeProvisionCallback = (fxValues: FxValues) => fxValues.initialCostsGuaranteeProvision;

  const getRealEstateAppraisalAmount = (offer: Offer) => offer.totals.realEstateAppraisal ?? 0;
  const fxRealEstateAppraisalCallback = (fxValues: FxValues) => fxValues.realEstateAppraisal;

  /** Merges two periods into a single period. Helper function for {@link getCombinedInsuranceCostPeriod}. */
  const mergePeriods = (monthlyPeriod?: PaymentDuration, nonMonthlyPeriod?: PaymentDuration): Period | undefined => {
    if (monthlyPeriod && nonMonthlyPeriod) {
      const from: number = Math.min(monthlyPeriod.from, nonMonthlyPeriod.from);
      const to: number = Math.max(monthlyPeriod.until, nonMonthlyPeriod.until);
      return { from, to };
    }

    if (monthlyPeriod) return { from: monthlyPeriod.from, to: monthlyPeriod.until };
    if (nonMonthlyPeriod) return { from: nonMonthlyPeriod.from, to: nonMonthlyPeriod.until };

    return undefined;
  };

  const getCombinedInsuranceCostPeriod = (
    offer: Offer,
    insuranceType: InsurancePaymentKeys & InitialInsurancePaymentKeys,
    isCredited: boolean,
  ): Period | undefined => {
    const monthlyPeriod: PaymentDuration | undefined = offer.monthlyFees[insuranceType]?.[0]?.period;
    const nonMonthlyPeriod: PaymentDuration | undefined = offer.nonMonthlyFees[insuranceType]?.[0]?.period;
    const initialTo: number | undefined = isCredited
      ? offer.creditedInitialCosts?.[insuranceType]?.coveredPeriod
      : offer.initialCosts[insuranceType]?.coveredPeriod;

    const mergedPeriod: Period | undefined = mergePeriods(monthlyPeriod, nonMonthlyPeriod);

    if (!mergedPeriod) return typeof initialTo === 'number' ? { from: 1, to: initialTo } : undefined;
    if (typeof initialTo !== 'number') return mergedPeriod;
    return { from: Math.min(mergedPeriod.from, 1), to: Math.max(mergedPeriod.to, initialTo) };
  };

  return {
    getCombinedCostsAmount,
    fxCombinedCostsCallback,
    getInterestAmount,
    fxInterestCallback,
    getProvisionAmount,
    fxProvisionCallback,
    getRealEstateInsuranceAmount,
    fxRealEstateInsuranceCallback,
    getLifeInsuranceAmount,
    fxLifeInsuranceCallback,
    getGuaranteeProvisionAmount,
    fxGuaranteeProvisionCallback,
    getRealEstateAppraisalAmount,
    fxRealEstateAppraisalCallback,
    getCombinedInsuranceCostPeriod,
  };
};
